import React, { useRef } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCheck, faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import "./altas-bajas.css";

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "0 B";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

function StepMarker({ tone = "dark" }) {
  return (
    <span className={`crm-altas-bajas-page__step-marker is-${tone}`}>
      <FontAwesomeIcon icon={faCheck} />
    </span>
  );
}

function StepHeading({ step, title, tone = "dark" }) {
  return (
    <div className="crm-altas-bajas-page__step-heading">
      <StepMarker tone={tone} />
      <div>
        <small>Paso {step}</small>
        <h3>{title}</h3>
      </div>
    </div>
  );
}

function LockedStepCard({ step, title, message }) {
  return (
    <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--locked">
      <div className="crm-altas-bajas-page__locked-row">
        <div className="crm-altas-bajas-page__locked-copy">
          <StepMarker tone="muted" />
          <div>
            <small>Paso {step}</small>
            <strong>{title}</strong>
          </div>
        </div>
        <div className="crm-altas-bajas-page__locked-hint">{message}</div>
      </div>
    </section>
  );
}

function CompletedStepCard({ step, title, message }) {
  return (
    <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--locked">
      <div className="crm-altas-bajas-page__locked-row">
        <div className="crm-altas-bajas-page__locked-copy">
          <StepMarker tone="success" />
          <div>
            <small>Paso {step}</small>
            <strong>{title}</strong>
          </div>
        </div>
        <div className="crm-altas-bajas-page__locked-hint">{message || "Paso completado."}</div>
      </div>
    </section>
  );
}

function StepOneCard({ wizardData, onUserChange, onSubmit, saving = false }) {
  return (
    <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--step-one">
      <Form onSubmit={onSubmit} className="crm-altas-bajas-page__step-one-grid">
        <div className="crm-altas-bajas-page__step-one-left">
          <StepHeading step={1} title="Blanqueo" tone="dark" />

          <div className="crm-altas-bajas-page__info-box">
            <p>* Crear o reutilizar usuario por CUIT.</p>
            <p>
              * El paso busca primero por CUIT en el flujo viejo. Si el usuario ya existe, lo reutiliza y actualiza;
              si no existe, lo crea.
            </p>
          </div>
        </div>

        <div className="crm-altas-bajas-page__step-one-right">
          <Form.Group>
            <Form.Label>Nombre / Razón Social</Form.Label>
            <Form.Control
              value={wizardData?.usuario?.nombre || ""}
              onChange={(event) => onUserChange("nombre", event.target.value)}
              placeholder="Nombre visible del usuario..."
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>CUIT</Form.Label>
            <Form.Control
              value={wizardData?.usuario?.cuit || ""}
              onChange={(event) => onUserChange("cuit", event.target.value)}
              maxLength={11}
              placeholder="Solo números"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={wizardData?.usuario?.email || ""}
              onChange={(event) => onUserChange("email", event.target.value)}
              placeholder="mail@comercio.com"
            />
          </Form.Group>

          <div className="crm-altas-bajas-page__step-actions">
            <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={saving}>
              {saving ? "Guardando..." : "Guardar y continuar"}
            </button>
          </div>
        </div>
      </Form>
    </section>
  );
}

function StepTwoCard(props) {
  const {
    wizardData,
    wizardLegajos = [],
    onSelectLegajo,
    onLegajoChange,
    onSubmitLegajo,
    loadingCatalogs = false,
    savingLegajo = false,
    onBack,
    catalogos,
    docDraft,
    setDocDraft,
    docTypes = [],
    onSubmitDoc,
    savingDoc = false,
    docs = [],
    onDeleteDoc,
    onContinue,
  } = props;
  const inputRef = useRef(null);

  return (
    <section className="crm-altas-bajas-page__card crm-altas-bajas-page__card--step-two">
      <div className="crm-altas-bajas-page__step-stack">
        <StepHeading step={2} title="Legajo" tone="dark" />

        <Form onSubmit={onSubmitLegajo} className="crm-altas-bajas-page__step-stack">
          {wizardLegajos.length ? (
            <Form.Group className="crm-altas-bajas-page__span-2">
              <Form.Label>Legajo existente para este usuario</Form.Label>
              <Form.Select value={wizardData?.legajoId || ""} onChange={onSelectLegajo}>
                <option value="">Crear uno nuevo</option>
                {wizardLegajos.map((item) => (
                  <option key={item.id ?? item.Id} value={item.id ?? item.Id}>
                    {item.nroLegajo ?? item.NroLegajo} - {item.nombreDeFantasia ?? item.NombreDeFantasia}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}

          <div className="crm-altas-bajas-page__form-grid">
            <Form.Group>
              <Form.Label>N.º de legajo sugerido</Form.Label>
              <Form.Control value={wizardData?.legajo?.nroLegajo || ""} onChange={(event) => onLegajoChange("nroLegajo", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Nombre de fantasía</Form.Label>
              <Form.Control value={wizardData?.legajo?.nombreDeFantasia || ""} onChange={(event) => onLegajoChange("nombreDeFantasia", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Terminal</Form.Label>
              <Form.Control value={wizardData?.legajo?.terminal || ""} onChange={(event) => onLegajoChange("terminal", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Tipo de persona</Form.Label>
              <Form.Select value={wizardData?.legajo?.tipoPersona || ""} onChange={(event) => onLegajoChange("tipoPersona", event.target.value)}>
                <option value="fisica">Física</option>
                <option value="juridica">Jurídica</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Nombre / Razón Social</Form.Label>
              <Form.Control value={wizardData?.legajo?.razonSocial || ""} onChange={(event) => onLegajoChange("razonSocial", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Provincia</Form.Label>
              <Form.Select value={wizardData?.legajo?.provincia || ""} onChange={(event) => onLegajoChange("provincia", event.target.value)}>
                <option value="">Seleccione</option>
                {(catalogos?.provincias || []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Localidad</Form.Label>
              <Form.Control value={wizardData?.legajo?.localidad || ""} onChange={(event) => onLegajoChange("localidad", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Dirección</Form.Label>
              <Form.Control value={wizardData?.legajo?.calle || ""} onChange={(event) => onLegajoChange("calle", event.target.value)} />
            </Form.Group>
            <div className="crm-altas-bajas-page__triple-grid crm-altas-bajas-page__span-2">
              <Form.Group>
                <Form.Label>Número</Form.Label>
                <Form.Control value={wizardData?.legajo?.numero || ""} onChange={(event) => onLegajoChange("numero", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Piso / Depto</Form.Label>
                <Form.Control value={wizardData?.legajo?.pisoDepto || ""} onChange={(event) => onLegajoChange("pisoDepto", event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Código postal</Form.Label>
                <Form.Control value={wizardData?.legajo?.codigoPostal || ""} onChange={(event) => onLegajoChange("codigoPostal", event.target.value)} />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>Email de contacto</Form.Label>
              <Form.Control type="email" value={wizardData?.legajo?.emailContacto || ""} onChange={(event) => onLegajoChange("emailContacto", event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Celular / WhatsApp</Form.Label>
              <Form.Control value={wizardData?.legajo?.celularWhatsapp || ""} onChange={(event) => onLegajoChange("celularWhatsapp", event.target.value)} />
            </Form.Group>
            <Form.Group className="crm-altas-bajas-page__span-2">
              <Form.Label>Asesor / Gestor</Form.Label>
              <Form.Select value={wizardData?.legajo?.asesor || ""} onChange={(event) => onLegajoChange("asesor", event.target.value)}>
                <option value="">Seleccione</option>
                {(catalogos?.asesores || []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="crm-altas-bajas-page__footer-row">
            <div className="crm-altas-bajas-page__helper-chip">* Guardar legajo y cargar documentos.</div>
            <div className="crm-altas-bajas-page__button-row">
              <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary" onClick={onBack}>
                Volver
              </button>
              <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={savingLegajo || loadingCatalogs}>
                {savingLegajo ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </Form>

        <section className="crm-altas-bajas-page__docs-section">
          <div className="crm-altas-bajas-page__docs-header">
            <div>
              <h4>Documentos del legajo</h4>
              <p>* Guardá el legajo para habilitar archivos</p>
            </div>
          </div>

          <Form onSubmit={onSubmitDoc} className="crm-altas-bajas-page__docs-grid">
            <Form.Group>
              <Form.Label>Tipo de documento</Form.Label>
              <Form.Select
                value={docDraft?.nombreDocumento || ""}
                onChange={(event) => setDocDraft((prev) => ({ ...prev, nombreDocumento: event.target.value }))}
                disabled={!wizardData?.legajoId}
              >
                {docTypes.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="crm-altas-bajas-page__upload-column">
              <Form.Label>Archivo</Form.Label>
              <input
                ref={inputRef}
                type="file"
                hidden
                onChange={(event) =>
                  setDocDraft((prev) => ({
                    ...prev,
                    file: event.target.files?.[0] || null,
                  }))
                }
              />
              <button
                type="button"
                className={`crm-altas-bajas-page__upload-box ${!wizardData?.legajoId ? "is-disabled" : ""}`}
                onClick={() => wizardData?.legajoId && inputRef.current?.click()}
                disabled={!wizardData?.legajoId}
              >
                <FontAwesomeIcon icon={faCloudArrowUp} />
                <strong>Seleccionar archivo</strong>
                <span>Peso máximo según validación vigente</span>
              </button>
              {docDraft?.file ? <div className="crm-altas-bajas-page__selected-file">{docDraft.file.name}</div> : null}
            </div>
            <div className="crm-altas-bajas-page__button-row crm-altas-bajas-page__span-2">
              <button type="submit" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary" disabled={!wizardData?.legajoId || savingDoc}>
                {savingDoc ? "Guardando..." : "Guardar archivo"}
              </button>
              <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={!wizardData?.legajoId} onClick={onContinue}>
                Continuar
              </button>
            </div>
          </Form>

          <div className="crm-altas-bajas-page__docs-table-wrap">
            <table className="crm-altas-bajas-page__docs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th>Tamaño</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {docs.length ? (
                  docs.map((item) => (
                    <tr key={item.idDocumento ?? item.IdDocumento}>
                      <td>{item.idDocumento ?? item.IdDocumento}</td>
                      <td>{item.nombreDocumento ?? item.NombreDocumento}</td>
                      <td>{item.documentoTipo ?? item.DocumentoTipo ?? "-"}</td>
                      <td>{formatFileSize(item.bytes ?? item.Bytes ?? 0)}</td>
                      <td>
                        <button type="button" className="crm-altas-bajas-page__table-link" onClick={() => onDeleteDoc(item.idDocumento ?? item.IdDocumento)}>
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="crm-altas-bajas-page__docs-empty">
                      No hay documentos cargados todavía.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
}

export default function AltasBajasWorkflow(props) {
  const {
    dateLabel,
    timeLabel,
    mode,
    onModeChange,
    onRefresh,
    isAltaAgregaMode,
    altaWizardStep,
    altaWizardStatus,
    altaWizardData,
    altaWizardLegajos,
    onUserChange,
    onSubmitBlanqueo,
    savingBlanqueo,
    onSelectLegajo,
    onLegajoChange,
    onSubmitLegajo,
    loadingCatalogs,
    savingLegajo,
    catalogos,
    onBackToStepOne,
    altaLegajoDocDraft,
    setAltaLegajoDocDraft,
    docTypes,
    onSubmitDoc,
    savingDoc,
    altaLegajoDocs,
    onDeleteDoc,
    onContinueToStepThree,
    stepThreeContent,
    bajaContent,
    modificarContent,
  } = props;

  return (
    <div className="crm-postventa-workflow crm-altas-bajas-page">
      <div className="crm-postventa-altas-header">
        <div className="crm-postventa-altas-header-copy">
          <span className="crm-postventa-kicker">CRM POSTVENTAS</span>
          <h2>Altas / Bajas</h2>
        </div>
        <div className="crm-postventa-altas-header-clock">
          <span>{dateLabel}</span>
          <span className="crm-postventa-altas-header-separator" />
          <span>{timeLabel}</span>
        </div>
      </div>

      <div className="crm-postventa-altas-tabs-row">
        <div className="crm-postventa-herramientas-tabs" role="tablist" aria-label="Modos de altas y bajas">
          <button type="button" className={`crm-postventa-herramientas-tab ${mode === "alta" ? "is-active" : ""}`} onClick={() => onModeChange("alta")}>
            Alta guiada
          </button>
          <button type="button" className={`crm-postventa-herramientas-tab ${mode === "agrega" ? "is-active" : ""}`} onClick={() => onModeChange("agrega")}>
            Agregar terminal a comercio existente
          </button>
          <button type="button" className={`crm-postventa-herramientas-tab ${mode === "baja" ? "is-active" : ""}`} onClick={() => onModeChange("baja")}>
            Baja por terminal
          </button>
          <button type="button" className={`crm-postventa-herramientas-tab ${mode === "modificar" ? "is-active" : ""}`} onClick={() => onModeChange("modificar")}>
            Modificar legajo
          </button>
        </div>
        <button type="button" className="crm-postventa-altas-refresh" onClick={onRefresh} aria-label="Actualizar">
          <FontAwesomeIcon icon={faArrowsRotate} />
        </button>
      </div>

      {mode === "baja" ? (
        bajaContent
      ) : mode === "modificar" ? (
        modificarContent
      ) : isAltaAgregaMode ? (
        stepThreeContent
      ) : (
        <div className="crm-altas-bajas-page__stack">
          {altaWizardStep === 1 ? (
            <>
              <StepOneCard wizardData={altaWizardData} onUserChange={onUserChange} onSubmit={onSubmitBlanqueo} saving={savingBlanqueo} />
              <LockedStepCard step={2} title="Legajo" message="* Completar Paso 1 para continuar con el Alta Guiada" />
              <LockedStepCard step={3} title="Alta CRM" message="* Completar Paso 1 y Paso 2 para continuar con el Alta Guiada" />
            </>
          ) : null}

          {altaWizardStep === 2 ? (
            <>
              <CompletedStepCard step={1} title="Blanqueo" message={altaWizardStatus?.blanqueo || "Paso completado."} />
              <StepTwoCard
                wizardData={altaWizardData}
                wizardLegajos={altaWizardLegajos}
                onSelectLegajo={onSelectLegajo}
                onLegajoChange={onLegajoChange}
                onSubmitLegajo={onSubmitLegajo}
                loadingCatalogs={loadingCatalogs}
                savingLegajo={savingLegajo}
                onBack={onBackToStepOne}
                catalogos={catalogos}
                docDraft={altaLegajoDocDraft}
                setDocDraft={setAltaLegajoDocDraft}
                docTypes={docTypes}
                onSubmitDoc={onSubmitDoc}
                savingDoc={savingDoc}
                docs={altaLegajoDocs}
                onDeleteDoc={onDeleteDoc}
                onContinue={onContinueToStepThree}
              />
              <LockedStepCard step={3} title="Alta CRM" message="* Completar Paso 1 y Paso 2 para continuar con el Alta Guiada" />
            </>
          ) : null}

          {altaWizardStep >= 3 ? (
            <>
              <CompletedStepCard step={1} title="Blanqueo" message={altaWizardStatus?.blanqueo || "Paso completado."} />
              <CompletedStepCard step={2} title="Legajo" message={altaWizardStatus?.legajo || "Paso completado."} />
              {stepThreeContent}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
